import { useEffect, useRef, useState } from "react";
import { useFilterStore } from "@/stores/filterStore";
import { getCached, isFresh, setCached } from "@/lib/queryCache";

// rangeOverride: {from, to} — 날짜 범위는 전역 상태가 아니라 호출하는 쪽(섹션)이 항상 소유한다
// (2026-07-15, 전역 DateRangePicker 제거 — 상세 배경은 stores/filterStore.js 참고).
//
// 60초 자동 재조회(poll)는 제거됨(2026-07-15) — 관리자가 화면을 보고 있는 동안 조용히 다시
// 불러오면서 로딩 상태로 깜빡이는 게 오히려 방해가 된다는 피드백.
//
// SWR 캐시(2026-07-18, "속도감" 피드백) — 캐시 키는 fetcher 소스 텍스트 + 파라미터.
// fetcher 는 호출부마다 인라인 화살표 함수라 toString() 이 호출부를 구분하는 안정적인
// 지문이 된다(같은 소스의 두 훅은 from/to 가 달라 키가 갈린다). 신선한 캐시(3분)는
// 네트워크를 생략하고, 오래된 캐시는 즉시 보여준 뒤 백그라운드로 갱신한다 — 두 경우 모두
// 로딩 스켈레톤 없이 바로 그려진다.
export function useAnalyticsQuery(fetcher, deps = [], rangeOverride) {
  const { pool, env } = useFilterStore();
  const from = rangeOverride?.from;
  const to = rangeOverride?.to;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    const key = `${fetcher.toString()}|${JSON.stringify([from, to, pool, env, deps])}`;
    const entry = getCached(key);

    if (entry) {
      // 캐시 히트 — 즉시 표시. 신선하면 여기서 끝(네트워크 0회).
      setData(entry.data);
      setError(null);
      setLoading(false);
      if (isFresh(entry)) return () => { cancelled = true; };
    } else {
      setLoading(true);
      setError(null);
    }

    async function run() {
      try {
        const r = await fetcher({ from, to, pool, env }, ctrl.signal);
        if (!cancelled) {
          setCached(key, r);
          setData(r);
          setError(null);
        }
      } catch (e) {
        // 캐시를 이미 보여주고 있는 백그라운드 갱신 실패는 조용히 무시 — 화면의 유효한
        // 데이터를 에러로 덮지 않는다.
        if (!cancelled && e.name !== "AbortError" && !entry) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to, pool, env, ...deps]);

  return { data, loading, error };
}
